// Home — "My Desk", the page the nav's My Desk item points at (path `/`,
// lib/nav.ts). SPEC-001 §12's "four-clinic week overview, needs-attention,
// live Eastern clock" is now one panel type among many on a canvas people
// resize and move directly (Request Ledger N-090, owner 2026-09-08, and
// N-090b the same day):
//   1. "Cards can be set in different sizes ... like a real canvas."
//   2. "Every manager gets their own canvas."
//   3. "Need to give them the ability to add various tables."
//   N-090b: "This canvas is terrible to manage and not very intuitive...
//   make boxes bigger horizontally and vertically" — replaced N-090's
//   Arrange-mode toggle and segmented size buttons with dragging a panel's
//   own edges and title bar; DeskAddMenu.tsx now owns "Add panel" and
//   "Reset layout" (a small button at the top-right of this page's header,
//   per the build spec), so this file no longer holds an `arrange` toggle
//   at all.
//
// This file no longer renders any panel content itself — every element that
// used to be hardcoded here (the four-clinic strip, needs-attention, the
// alert cards, Canvas/People/Quick Docs) is now a catalog entry
// (client/src/desk/panelCatalog.tsx) rendered by DeskGrid.tsx from a saved
// `DeskLayout`. Home's own job shrank to: load that layout (falling back to
// the role's shipped default), hold it in local state so an edit paints
// instantly, and save on change.
//
// Reads ONLY from the store for the WEEK data (INV-1: navigation costs zero
// network calls) — the desk layout itself is a per-user resource primed at
// bootstrap exactly like favorites (lib/bootstrap.ts), so this page still
// costs nothing extra on ordinary navigation.
import type { JSX } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { clinicNow } from '../lib/clinicDay';
import { state } from '../lib/store';
import { snapshot } from '../lib/bootstrap';
import { formatDate } from '../lib/date';
import { FaultState, EmptyState } from '../components/FaultState';
import { session } from '../lib/session';
import type { Role } from '../lib/nav';
import { useDeskLayout, scheduleDeskLayoutSave, saveDesksLocked } from '../lib/deskLayout';
import { defaultLayoutFor } from '../desk/defaultLayouts';
import { ownerDeskFor, type DeskLayout } from '../desk/panelCatalog';
import { DeskGrid } from '../components/DeskGrid';
import { DeskAddMenu } from '../components/DeskAddMenu';
import { WhatsNewCards } from '../components/WhatsNewCard';
import { viewAsLocation } from '../lib/viewAs';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';

/** How long the "Saved" caption stays up once a save actually completes
 * (build spec item 5: "a tiny 'Saved' caption fades in the page header for
 * 2 s"). */
const SAVED_FLASH_MS = 2000;

// Re-exported so callers that used to import the four-clinic summary logic
// straight off Home.tsx keep working — the logic itself now lives in
// ClinicOverviewStrip.tsx, the `clinicStrip` panel type.
export { summarise, type ClinicSummary } from '../components/ClinicOverviewStrip';

function knownRole(role: string | null | undefined): Role | null {
  return role === 'OWNER' || role === 'DIRECTOR' || role === 'MANAGER' ? role : null;
}

export function HomePage(): JSX.Element {
  const snap = snapshot.value;
  const tasks = Object.keys(state.value.task ?? {}).length;
  const workItems = Object.keys(state.value.workItem ?? {}).length;
  const me = session.value.me;
  const role = knownRole(me?.role);
  const isOwner = role === 'OWNER';
  // The owner's view-as lens (lib/viewAs.ts): My Desk shows exactly what
  // that clinic's manager sees — his own Desk as a MANAGER sees it,
  // read-only (owner, 2026-10-12).
  const lensCode = isOwner && me?.viewingAs ? viewAsLocation.value : null;
  const lensName = lensCode ? (LOCATION_NAMES[lensCode as LocationCode] ?? lensCode) : null;

  const resource = useDeskLayout();
  // Local edits since the resource last loaded — set on every change, so a
  // drag/resize/add/remove paints instantly rather than waiting on the
  // debounced PUT to round-trip. Derived synchronously (no effect) so the
  // FIRST render already shows the loaded layout, or the role's shipped
  // default (N-090: "the shipped default is only the starting point") when
  // the person has never customised their canvas.
  const [edited, setEdited] = useState<DeskLayout | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockOverride, setLockOverride] = useState<boolean | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);
  const savedFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const data = resource.status === 'ready' ? resource.data : null;
  // Owner, 2026-10-12: "I want to edit the layout for myself and that
  // carries to everyone." A manager's or director's Desk is Tomer's own as
  // their role sees it (the server already sends it that way; ownerDeskFor
  // is idempotent, and builds it from his shipped default when he never
  // saved one). LOCK DESK: shown exactly, nothing to move, nothing saved.
  const locked = !isOwner && data?.locked === true;
  let baseLayout: DeskLayout | null = null;
  if (data && role) {
    baseLayout = data.fromOwner
      ? ownerDeskFor(data.layout ?? defaultLayoutFor('OWNER'), role)
      : (data.layout ?? defaultLayoutFor(role));
  }
  const ownLayout = locked ? baseLayout : (edited ?? baseLayout);
  const layout = lensCode && ownLayout ? ownerDeskFor(ownLayout, 'MANAGER') : ownLayout;
  const gridRole: Role | null = lensCode ? 'MANAGER' : role;
  const readOnly = locked || Boolean(lensCode);
  // Reset layout for an unlocked manager or director: back to Tomer's Desk.
  const resetTo = !isOwner && role && data && 'ownerLayout' in data
    ? ownerDeskFor(data.ownerLayout ?? defaultLayoutFor('OWNER'), role)
    : undefined;
  // What the server last said about the lock, held here too: a desk-layout
  // read that started BEFORE the click can land after it and carry the old
  // value into the cache, so the button would flip back. The answer to our
  // own PUT wins until a read agrees with it.
  const serverLocked = data?.desksLocked !== false;
  const desksLocked = lockOverride ?? serverLocked;

  function flashSaved(): void {
    if (savedFlashTimer.current) clearTimeout(savedFlashTimer.current);
    setShowSaved(true);
    savedFlashTimer.current = setTimeout(() => setShowSaved(false), SAVED_FLASH_MS);
  }

  function handleLayoutChange(next: DeskLayout): void {
    if (readOnly) return;
    setEdited(next);
    scheduleDeskLayoutSave(next, flashSaved);
  }

  async function toggleLock(): Promise<void> {
    setLockBusy(true);
    setLockError(null);
    try {
      setLockOverride(await saveDesksLocked(!desksLocked));
    } catch (err) {
      setLockError(err instanceof Error ? err.message : String(err));
    } finally {
      setLockBusy(false);
    }
  }

  return (
    <div class="main">
      <div class="page-header">
        <div class="section-head-row">
          <h1>My Desk</h1>
          {role ? (
            <div class="section-head-actions">
              {layout && !readOnly ? (
                <>
                  <span
                    class={`desk-saved-flash${showSaved ? ' desk-saved-flash-on' : ''}`}
                    data-testid="desk-saved-flash"
                    aria-live="polite"
                  >
                    {showSaved ? 'Saved' : ''}
                  </span>
                  <DeskAddMenu layout={layout} role={role} onChange={handleLayoutChange} resetTo={resetTo} />
                </>
              ) : null}
              {isOwner && !lensCode && data ? (
                <button
                  type="button"
                  class="btn"
                  data-testid="desk-lock"
                  disabled={lockBusy}
                  onClick={() => { void toggleLock(); }}
                >
                  {desksLocked ? 'Unlock desks' : 'Lock desks'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {snap.faults.map((f) => (
        <FaultState key={f.slice} name={`${f.slice} did not load`} detail={f.detail} />
      ))}

      {snap.loading && !snap.loadedAt ? <EmptyState label="Loading this week..." /> : null}

      {!snap.loading && !tasks && !workItems && !snap.faults.length ? (
        <EmptyState label="Nothing loaded for this week yet." />
      ) : null}

      {/* What's New (owner, 2026-10-06): above the grid until read. */}
      {role ? <WhatsNewCards /> : null}

      {isOwner && !lensCode && data ? (
        <p class="muted desk-viewas-note" data-testid="desk-lock-note">
          {desksLocked
            ? 'Managers and the director see this Desk (locked).'
            : 'Unlocked: managers and the director start from this Desk and may arrange their own.'}
        </p>
      ) : null}
      {lockError ? <FaultState name="the desks were not locked or unlocked" detail={lockError} /> : null}

      {lensName ? (
        <p class="muted desk-viewas-note" data-testid="desk-viewas-note">
          Viewing as {lensName} — this is what the manager sees.
        </p>
      ) : null}

      {locked ? (
        <p class="muted desk-viewas-note" data-testid="desk-locked-note">Your Desk is set by Tomer.</p>
      ) : null}

      {gridRole && layout ? (
        <DeskGrid layout={layout} role={gridRole} onChange={handleLayoutChange} readOnly={readOnly} />
      ) : null}

      {snap.loadedAt ? (
        <p class="page-note">
          {/* The DATE has to come from the same Eastern clock as the time
              beside it. It was formatted in the browser's zone, so read from
              seven hours ahead this line said "Loaded 08/17 … 6:10 PM Eastern"
              — the 17th at a moment that was still the 16th in Florida. */}
          Loaded {formatDate(clinicNow(new Date(snap.loadedAt)))} at{' '}
          {new Date(snap.loadedAt).toLocaleTimeString('en-US', { timeZone: 'America/New_York' })} Eastern.
        </p>
      ) : null}
    </div>
  );
}
